import { computed, ref } from 'vue'
import { getResources } from '@/resources/api'
import { getStrategiesForUuids } from '@/strategies/api'
import { getStandardMatches, getValidStandardSets, type StandardMatch } from '@/standards/api'
import type { StandardSetRef } from '@/standards/standard_types'
import { AccessPointsUtil } from '@/util/access_points'
import type { ObjectiveLevel } from '@/goals/objectives/objective_levels'
import type { SelectedResource } from './SelectResources.vue'
import type { SelectedStrategy } from './SelectStrategies.vue'
import type { SubjectAlignment } from './SelectSubjects.vue'
import { getGoalCharacteristics, getSubjectsDomainsGrades, type GoalCharacteristic } from './access_points_api'
import {
  deleteDraft,
  generateAccessPoints,
  getAnchorPageForEditing,
  getDraftStatus,
  publishDraft,
  updateAccessPoints,
  type EditableAccessPoint,
  type EditableAnchorPage,
  type EditableObjective,
  type SaveResponse
} from './anchor_page_editor_api'
import { useAdminErrors } from './use_admin_errors'
import { useBufferedSave } from './use_buffered_save'

export interface CommonFields {
  author_id: string
  title: string
  subject_alignments: SubjectAlignment[]
  grades: string[]
  adaptation_strategies: string[]
  published: boolean | null
  score: number | null
  standard_set_refs: StandardSetRef[]
  universal: boolean | null
  published_at: string | null
  characteristics: string[]
}

const QUARTERS: ObjectiveLevel[] = ['1st', '2nd', '3rd']

const pad = (value: number) => String(value).padStart(2, '0')

// Vue 2 formatted these with moment's 'M/D/YY H:mm A', a 24-hour clock with AM/PM after it.
export function formatEditorTime(value: string | undefined | null): string {
  const date = new Date(value ?? '')
  if (Number.isNaN(date.getTime())) return 'Invalid date'
  const hours = date.getHours()
  return `${date.getMonth() + 1}/${date.getDate()}/${pad(date.getFullYear() % 100)} ${hours}:${pad(date.getMinutes())} ${hours < 12 ? 'AM' : 'PM'}`
}

export const emptyCommonFields = (): CommonFields => ({
  author_id: '',
  title: '',
  subject_alignments: [],
  grades: [],
  adaptation_strategies: [],
  published: null,
  score: 0,
  standard_set_refs: [],
  universal: null,
  published_at: null,
  characteristics: []
})

function orderedAccessPoints(page: EditableAnchorPage): EditableAccessPoint[] {
  const gradeLevel: EditableAccessPoint = {
    access_point_level: page.access_point_level,
    uuid: page.uuid,
    author_id: page.author_id,
    goal_text: page.goal_text,
    goal_resources: page.goal_resources,
    resource_languages: page.resource_languages,
    objectives: page.objectives,
    objective_resources: page.objective_resources ?? {},
    assessment_items: page.assessment_items ?? {},
    updated_at: page.updated_at
  }
  const byLevel = (level: string) => page.access_points.find(accessPoint => accessPoint.access_point_level === level)
  return [gradeLevel, byLevel(AccessPointsUtil.valuesMap.APPROACHING), byLevel(AccessPointsUtil.valuesMap.DEVELOPING)]
    .filter((accessPoint): accessPoint is EditableAccessPoint => Boolean(accessPoint))
}

export function formatObjectivesForUpdate(objectives: EditableObjective[] = []) {
  return objectives.map(({ level, text, title, uuid, resources, created_at }) => ({ level, text, title, uuid, resources, created_at }))
}

export function useAnchorPageEditor(uuid: () => string) {
  const isLoading = ref(false)
  const isDraft = ref(false)
  const common = ref<CommonFields>(emptyCommonFields())
  const accessPoints = ref<EditableAccessPoint[]>([])
  const standardsForSet = ref<Record<string, string[]>>({})
  const validStandardSets = ref<string[]>([])
  const standardsMap = ref<Record<string, StandardMatch>>({})
  const resourcesMap = ref<Record<string, SelectedResource & { uuid: string }>>({})
  const strategiesMap = ref<Record<string, SelectedStrategy>>({})
  const characteristicsMap = ref<Record<string, GoalCharacteristic>>({})
  const subjects = ref<string[]>([])
  const subjectDomainsMap = ref<Record<string, string[]>>({})
  const subjectGradesMap = ref<Record<string, string[]>>({})
  const { errors, notifyError, dismissError } = useAdminErrors()
  const isLoaded = ref(false)
  // Each lookup keeps only its newest response, so switching pages or adding quickly can't
  // leave an older, shorter answer on screen.
  const latestRequest: Record<string, number> = {}
  const startRequest = (name: string) => (latestRequest[name] = (latestRequest[name] ?? 0) + 1)
  const isLatest = (name: string, id: number) => latestRequest[name] === id
  let characteristicsList: Promise<GoalCharacteristic[]> | null = null

  // A save can flush after the editor has moved to another page, so each page's updates go out with
  // the author that page was loaded with rather than whichever page is showing by then.
  const authorIdByGradeLevelUuid = new Map<string, string>()

  const { isSaving, pushUpdate: pushPendingUpdate, flushUpdates } = useBufferedSave({
    save: ({ gradeLevelUuid, uuids, updates }) => updateAccessPoints(gradeLevelUuid, uuids, updates, authorIdByGradeLevelUuid.get(gradeLevelUuid) ?? common.value.author_id),
    onSaved: (response: SaveResponse) => {
      accessPoints.value = accessPoints.value.map(accessPoint =>
        response[accessPoint.uuid] ? { ...accessPoint, updated_at: response.updated_at as string } : accessPoint
      )
    },
    onError: ({ uuids }, error) => notifyError(`Error saving for ${uuids.join(', ')}`, error),
    afterFlush: () => { if (isLoaded.value) checkDraftStatus() }
  })

  function pushUpdate(update: { uuids: string[], updates: Record<string, unknown> }, flushImmediately = false) {
    pushPendingUpdate({ ...update, gradeLevelUuid: uuid() }, flushImmediately)
  }

  const mostRecentUpdatedAt = computed(() =>
    formatEditorTime([...accessPoints.value.map(accessPoint => accessPoint.updated_at)].sort().at(-1))
  )
  const areAccessPointsMissing = computed(() => Boolean(common.value.title) && accessPoints.value.length < 3)

  function saveCommonData(updates: Record<string, unknown>, flushImmediately = false) {
    pushUpdate({ uuids: accessPoints.value.map(accessPoint => accessPoint.uuid), updates }, flushImmediately)
  }

  async function checkDraftStatus() {
    const requestId = startRequest('draftStatus')
    try {
      const status = await getDraftStatus(uuid())
      if (!isLatest('draftStatus', requestId)) return
      if (status.draft === true) isDraft.value = true
      common.value = { ...common.value, published_at: formatEditorTime(status.published_at) }
    } catch (error) {
      console.error('Failed to check the draft status:', error)
    }
  }

  async function populateStandardsMapForSet(setCode: string) {
    const refs = standardsForSet.value[setCode] ?? []
    if (!refs.length) return
    try {
      const standards = await getStandardMatches(setCode, refs)
      standardsMap.value = { ...standardsMap.value, ...Object.fromEntries(standards.map(standard => [standard.standard_long_ref, standard])) }
    } catch (error) {
      notifyError(`Could not get ${setCode} standards`, error)
    }
  }

  function allResourceUuids(): string[] {
    return accessPoints.value.flatMap(accessPoint => [
      ...(accessPoint.goal_resources ?? []),
      ...Object.values(accessPoint.objective_resources ?? {}).flat(),
      ...(accessPoint.objectives ?? []).flatMap(objective => objective.resources ?? [])
    ])
  }

  async function populateResourcesMap() {
    const uuids = allResourceUuids()
    if (!uuids.length) return
    const requestId = startRequest('resources')
    try {
      const resources = await getResources(uuids)
      if (!isLatest('resources', requestId)) return
      resourcesMap.value = Object.fromEntries(resources.map(resource => [resource.uuid, resource as SelectedResource & { uuid: string }]))
    } catch (error) {
      notifyError('Could not get resources', error)
    }
  }

  // Resource languages can change after a page was saved; bring each access point's list up to date.
  function checkAndSetGoalLanguages(goals: Pick<EditableAccessPoint, 'uuid' | 'goal_resources' | 'resource_languages'>[]) {
    goals.forEach(goal => {
      const languages = Object.entries(resourcesMap.value)
        .filter(([resourceUuid]) => goal.goal_resources?.includes(resourceUuid))
        .map(([, resource]) => resource.language)
      const uniqueLanguages = [...new Set(languages)].sort()
      const current = goal.resource_languages ?? []
      if (JSON.stringify(uniqueLanguages) !== JSON.stringify([...current].sort())) {
        pushUpdate({ uuids: [goal.uuid], updates: { resource_languages: uniqueLanguages } })
      }
    })
  }

  async function populateStrategiesMap() {
    const uuids = common.value.adaptation_strategies
    if (!uuids.length) return
    const requestId = startRequest('strategies')
    try {
      const strategies = await getStrategiesForUuids(uuids)
      if (!isLatest('strategies', requestId)) return
      strategiesMap.value = Object.fromEntries(strategies.map(strategy => [strategy.uuid, strategy as unknown as SelectedStrategy]))
    } catch (error) {
      notifyError('Could not get strategies', error)
    }
  }

  async function populateCharacteristicsMap() {
    const uuids = common.value.characteristics
    if (!uuids.length) return
    const requestId = startRequest('characteristics')
    try {
      // The full list rarely changes, so fetch it once per page, as Vue 2's memory cache did.
      characteristicsList ??= getGoalCharacteristics()
      const characteristics = (await characteristicsList).filter(characteristic => uuids.includes(characteristic.Uuid))
      if (!isLatest('characteristics', requestId)) return
      characteristicsMap.value = Object.fromEntries(characteristics.map(characteristic => [characteristic.Uuid, characteristic]))
    } catch (error) {
      characteristicsList = null
      notifyError('Could not get characteristics', error)
    }
  }

  async function load(): Promise<{ legacyGoalUuid?: string, gradeLevelUuid?: string }> {
    const requestId = startRequest('page')
    isLoading.value = true
    isLoaded.value = false
    try {
      const page = await getAnchorPageForEditing(uuid())
      if (!isLatest('page', requestId)) return {}
      if (!page.access_grade_level_uuid) return { legacyGoalUuid: page.uuid }
      if (page.uuid !== page.access_grade_level_uuid) return { gradeLevelUuid: page.access_grade_level_uuid }

      accessPoints.value = orderedAccessPoints(page)
      common.value = {
        ...emptyCommonFields(),
        title: page.title,
        subject_alignments: page.subject_alignments ?? [],
        grades: page.grades ?? [],
        adaptation_strategies: page.adaptation_strategies ?? [],
        published: page.published,
        score: page.score,
        standard_set_refs: page.standard_set_refs ?? [],
        universal: page.universal,
        author_id: page.author_id ?? '',
        characteristics: page.characteristics ?? []
      }
      authorIdByGradeLevelUuid.set(page.uuid, common.value.author_id)

      standardsForSet.value = {}
      common.value.standard_set_refs.forEach(({ standard_set_code, standard_long_ref }) => {
        standardsForSet.value[standard_set_code] = [...(standardsForSet.value[standard_set_code] ?? []), standard_long_ref]
      })
      standardsMap.value = {}
      Object.keys(standardsForSet.value).forEach(setCode => populateStandardsMapForSet(setCode))
      await populateResourcesMap()
      if (!isLatest('page', requestId)) return {}
      checkAndSetGoalLanguages([page, ...page.access_points])
      populateStrategiesMap()
      populateCharacteristicsMap()
      checkDraftStatus()
      isLoaded.value = true
    } catch (error) {
      if (isLatest('page', requestId)) notifyError('Could not get content', error)
    } finally {
      if (isLatest('page', requestId)) isLoading.value = false
    }
    return {}
  }

  async function loadOptions() {
    try {
      const options = await getSubjectsDomainsGrades()
      subjects.value = options.subjects
      subjectDomainsMap.value = options.subjectDomainsMap
      subjectGradesMap.value = options.subjectGradesMap
    } catch (error) {
      notifyError('Could not get subjects', error)
    }
    try {
      validStandardSets.value = await getValidStandardSets()
    } catch (error) {
      notifyError('Could not get standard sets', error)
    }
  }

  function updateStandardsForSet(setCode: string, refs: string[]) {
    standardsForSet.value = { ...standardsForSet.value, [setCode]: refs }
    populateStandardsMapForSet(setCode)
    const standardSetRefs = Object.entries(standardsForSet.value).flatMap(([code, setRefs]) =>
      setRefs.map(ref => ({ standard_set_code: code, standard_long_ref: ref }))
    )
    saveCommonData({ standard_set_refs: standardSetRefs })
  }

  async function addLanguage(resourceUuid: string, accessPointUuid: string, languages: string[]) {
    if (!(resourceUuid in resourcesMap.value)) await populateResourcesMap()
    const language = resourcesMap.value[resourceUuid]?.language
    if (language !== undefined && !languages.includes(language)) {
      pushUpdate({ uuids: [accessPointUuid], updates: { resource_languages: [...languages, language] } })
    }
  }

  function generateObjectives(accessPoint: EditableAccessPoint) {
    if (accessPoint.objectives?.length) return
    accessPoint.objectives = QUARTERS.map(level => ({ level, title: '', text: '' }))
    accessPoint.assessment_items = Object.fromEntries(QUARTERS.map(level => [level, []]))
    accessPoint.objective_resources = Object.fromEntries(QUARTERS.map(level => [level, []]))
  }

  function clearObjectives(accessPoint: EditableAccessPoint) {
    accessPoint.objectives = []
    accessPoint.objective_resources = {}
    accessPoint.assessment_items = {}
    pushUpdate({ uuids: [accessPoint.uuid], updates: { objectives: null, objective_resources: null, assessment_items: null } })
  }

  function saveObjectives(accessPoint: EditableAccessPoint) {
    pushUpdate({ uuids: [accessPoint.uuid], updates: { objectives: formatObjectivesForUpdate(accessPoint.objectives) } })
  }

  async function tryOrNotify(action: () => Promise<unknown>, errorTitle: string) {
    try {
      await action()
      return true
    } catch (error) {
      notifyError(errorTitle, error)
      return false
    }
  }

  const generateMissingAccessPoints = () => tryOrNotify(() => generateAccessPoints(uuid()), 'Error generating missing access points')

  // A status check sent before the draft was published or deleted would otherwise answer that it still exists.
  function clearDraft() {
    startRequest('draftStatus')
    isDraft.value = false
  }

  async function saveDraft() {
    const isSaved = await tryOrNotify(() => publishDraft(uuid()), 'Error saving draft changes')
    if (isSaved) clearDraft()
    return isSaved
  }

  async function discardDraft() {
    const isDiscarded = await tryOrNotify(() => deleteDraft(uuid()), 'Error deleting draft changes')
    if (isDiscarded) clearDraft()
    return isDiscarded
  }

  function setPublished(isPublished: boolean) {
    common.value = { ...common.value, published: isPublished }
    // Publishing is a button press, so it saves now rather than after the typing pause.
    saveCommonData({ published: isPublished }, true)
  }

  return {
    isLoading, isDraft, isSaving, common, accessPoints, standardsForSet, validStandardSets, standardsMap,
    resourcesMap, strategiesMap, characteristicsMap, subjects, subjectDomainsMap, subjectGradesMap, errors,
    mostRecentUpdatedAt, areAccessPointsMissing,
    load, loadOptions, notifyError, dismissError, pushUpdate, flushUpdates, saveCommonData, updateStandardsForSet,
    populateResourcesMap, populateStrategiesMap, populateCharacteristicsMap, addLanguage,
    generateObjectives, clearObjectives, saveObjectives, generateMissingAccessPoints, saveDraft, discardDraft, setPublished
  }
}
